import { Spin } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { ROLL_CALL_API_BASE_URL } from "../RollCallConfig/RollCallConfig";
const AddSwipeLocationName = ({
  isOpen,
  onClose,
  record,
  existingName,
  onSave,
}) => {
  const [locationName, setLocationName] = useState("");
  const [canShare, setCanShare] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocationName(existingName || "");
      setCanShare(true);
    }
  }, [isOpen, existingName]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) handleCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    const offcanvasEl = document.getElementById("offcanvasSwipeLocationName");
    const handleHidden = () => resetForm();
    if (offcanvasEl) {
      offcanvasEl.addEventListener("hidden.bs.offcanvas", handleHidden);
    }
    return () => {
      if (offcanvasEl)
        offcanvasEl.removeEventListener("hidden.bs.offcanvas", handleHidden);
    };
  }, []);

  const resetForm = () => {
    setLocationName("");
    setCanShare(true);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!locationName.trim() || !record) return;

    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");

    setSubmitLoading(true);

    try {
      const requestBody = {
        SuperId: superId,
        Lat: record.lat,
        Long: record.lon,
        Name: locationName.trim(),
        Location: record["Swipe Location"],
        RefId: record.SwipeLocationId,
        CreatedBy: userData?.UserId || 0,
        CanShare: canShare ? 1 : 0,
      };

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/reports/addswipelocationname`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      const data = await response.json();

      if (data.status) {
        Swal.fire({
          title: "Success",
          text: "Location name saved successfully",
          icon: "success",
        }).then(() => {
          onSave(locationName.trim(), record);
          resetForm();
          onClose();
        });
      } else {
        Swal.fire({
          title: "Error",
          text: data?.ErrorMsg || "Failed to save location name.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Please try again.",
        icon: "error",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!isOpen) return null;

  const lat = record?.lat;
  const lon = record?.lon;

  return (
    <div>
      <style>{`
        #offcanvasSwipeLocationName { width: 420px !important; }
        @media (max-width: 576px) {
          #offcanvasSwipeLocationName { width: 90% !important; }
        }
      `}</style>

      {submitLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1060,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255,255,255,0.2)",
          }}
        >
          <Spin size="large" tip="Saving..." />
        </div>
      )}

      <div
        className="offcanvas-backdrop fade show"
        onClick={handleCancel}
        style={{ zIndex: 1040 }}
      />

      <div
        className="offcanvas offcanvas-end show"
        id="offcanvasSwipeLocationName"
        style={{ visibility: "visible", zIndex: 1045 }}
      >
        <div className="offcanvas-header">
          <h4>{existingName ? "Edit Location Name" : "Add Location Name"}</h4>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={handleCancel}
            aria-label="Close"
          />
        </div>

        <hr className="m-0 p-0" />

        <div className="offcanvas-body">
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Swipe Location</label>
                <input
                  type="text"
                  className="form-control"
                  value={record?.["Swipe Location"] || ""}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa" }}
                />
              </div>

              <div className="col-12">
                {lat && lon ? (
                  <div
                    className="border rounded overflow-hidden"
                    style={{ height: "220px" }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${lat},${lon}&hl=en&z=15&output=embed`}
                    />
                  </div>
                ) : (
                  <div className="alert alert-warning py-3 text-center">
                    Location coordinates not available
                  </div>
                )}
              </div>

              <div className="col-12">
                <label className="form-label">
                  <span className="text-danger me-1">*</span>Location Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Office D-Block, Main Gate, Warehouse A"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  maxLength={60}
                  required
                  autoFocus
                />
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-muted d-none">
                    Applies to this swipe location record.
                  </small>
                  <small className="text-muted">{locationName.length}/60</small>
                </div>
              </div>

              <div className="col-12">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="canShareCheck"
                    checked={canShare}
                    onChange={(e) => setCanShare(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="canShareCheck">
                    Share this location name with others
                  </label>
                </div>
              </div>

              <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={!locationName.trim() || submitLoading}
                >
                  {submitLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

AddSwipeLocationName.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  existingName: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

export default AddSwipeLocationName;
